import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import WarningIcon from '@mui/icons-material/Warning';

import {
    loadContractDetailsForContractIds,
    loadServiceDetailsForContractId
} from '../../../store/directusService';
import {
    clearBusinessLogicError,
    clearValidationErrors,
    createParticipantContactCentre,
    loadContractConstants,
    loadPostcodeServiceTeamMapping
} from '../../../store/participantService';
import { loadTeam, loadTeamsForService } from '../../../store/userService';
import { getObjFromId } from '../../../utils/directusFunctions';
import {
    ADDRESS_LINE_1_MAX_LENGTH,
    ADDRESS_LINE_2_MAX_LENGTH,
    ADDRESS_LINE_3_MAX_LENGTH,
    CITY_MAX_LENGTH,
    MAX_EMAIL,
    MAX_NAME,
    MAX_PHONE,
    MAX_POSTCODE,
    names
} from '../../../utils/formValidation/commonConstants';
import { CONTRACT_DETAILS_ERROR } from '../../../utils/formValidation/loadingErrorMessageConstants';
import { regExps } from '../../../utils/formValidation/validationPatterns';
import { uuid } from '../../../utils/stringUtils';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import RadioButtons from '../../formElements/RadioButtons';
import SingleSelect from '../../formElements/SingleSelect';
import TextInputField from '../../formElements/TextInputField';
import LoadingSpinner from '../../ui/LoadingSpinner';
import ScreenError from '../../ui/messageBanners/ScreenError';

import {
    getPostcodeInputErrors,
    getSubmitErrors
} from './validateCreateParticipantContactCentre';

import app from '../../../app.module.css';
import form from '../../../commonStyles/formStyles.module.css';
import local from './createParticipantContactCentre.module.css';

//CONSTANTS
const acceptedRoles = [ADVISER, RECRUITMENT_MANAGER, MANAGER, QUALITY, SUPERUSER];
const _MESSAGE = 'New participant created';
const _POSTCODE_WARNING_TEXT = {
    allocateDefault:
        'Postcode not found, participant will be allocated to the contract’s default service and team',
    manualSelect: 'Postcode not found, please select a service and team'
};

const CreateParticipantContactCentre = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const initialState = {
        firstName: '',
        middleName: '',
        lastName: '',
        address1: '',
        address2: '',
        address3: '',
        city: '',
        defaultTeam: null,
        postcode: '',
        primaryPhoneNumber: '',
        emailAddress: '',
        useTemporaryUniqueId: false,
        uniqueIdentifier: '',
        repeatUniqueIdentifier: '',
        uniqueIdentifierType: null,
        referralDate: new Date().toISOString().slice(0, 10),
        contractId: '',
        serviceId: '',
        teamId: ''
    };

    const [newEntry, setNewEntry] = useState(() => initialState);
    const [resetKey, setResetKey] = useState(() => uuid());
    const [errors, setErrors] = useState({});
    const [disableFields, setDisableFields] = useState(false);
    const [selectedContractDetails, setSelectedContractDetails] = useState({});
    const [services, setServices] = useState([]);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [postcodeWarning, setPostcodeWarning] = useState('');
    const [pendingAutoTeamId, setPendingAutoTeamId] = useState('');

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const { roles } = loggedInUser;
    const teamsForService = useSelector((state) => state.entities.userService.teamsForService);
    const serviceDetailsForContractId = useSelector(
        (state) => state.entities.directusService.serviceDetailsForContractId
    );
    const contractDetails = useSelector((state) => state.entities.directusService.contractDetails);
    const contractConstants = useSelector(
        (state) => state.entities.participantService.contractConstants
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);
    const errorMessage = useSelector((state) => state.entities.formsState.errorMessage);
    const validationErrors = useSelector(
        (state) => state.entities.participantService.validationErrors
    );
    const businessLogicError = useSelector(
        (state) => state.entities.participantService.businessLogicError
    );
    const postcodeServiceTeamMapping = useSelector(
        (state) => state.entities.participantService.postcodeServiceTeamMapping
    );
    const currentTeam = useSelector((state) => state.entities.userService.currentTeam);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );

    // USE EFFECTS
    useEffect(() => {
        if (!loggedInUser?.contractIds) return;
        dispatch(loadContractDetailsForContractIds(loggedInUser.contractIds));
    }, [loggedInUser]);

    useEffect(() => {
        setDisableFields(!hasRole(acceptedRoles, roles));
        setIsButtonDisabled(!hasRole(acceptedRoles, roles));
    }, [roles]);

    useEffect(() => {
        if (newEntry.contractId && contractDetails?.length > 0) {
            const contract = contractDetails.find((el) => el.id === newEntry.contractId);
            setSelectedContractDetails(contract);
            onUpdate('uniqueIdentifierType', contract?.unique_identifier_type.name);
            dispatch(loadServiceDetailsForContractId(newEntry.contractId));
            dispatch(loadContractConstants(newEntry.contractId));
        }
    }, [newEntry.contractId, contractDetails]);

    useEffect(() => {
        if (!loggedInUser.serviceIds?.length || !serviceDetailsForContractId?.length) {
            setServices([]);
        } else {
            setServices(
                serviceDetailsForContractId.filter((el) => loggedInUser.serviceIds.includes(el.id))
            );
        }
    }, [serviceDetailsForContractId]);

    useEffect(() => {
        if (!newEntry.contractId || !teamsForService.length || newEntry.teamId) return;

        const hasMappedTeamSelection =
            contractConstants.contactCentreCreateScreenPostCodeLookup &&
            postcodeServiceTeamMapping?.serviceId &&
            postcodeServiceTeamMapping?.teamId;

        if (hasMappedTeamSelection || pendingAutoTeamId) return;

        if (teamsForService.some((el) => el.id === contractConstants.defaultTeamId)) {
            onUpdate('teamId', contractConstants.defaultTeamId);
        }
    }, [
        teamsForService,
        newEntry.contractId,
        newEntry.teamId,
        contractConstants.defaultTeamId,
        contractConstants.contactCentreCreateScreenPostCodeLookup,
        postcodeServiceTeamMapping,
        pendingAutoTeamId
    ]);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (newEntry.postcode) {
            if (contractConstants.contactCentreCreateScreenPostCodeLookup) {
                if (regExps.POSTCODE_R.test(newEntry.postcode)) {
                    dispatch(
                        loadPostcodeServiceTeamMapping(newEntry.postcode, newEntry.contractId)
                    );
                } else if (newEntry.serviceId) {
                    onUpdateContract(newEntry.contractId);
                }
            }
        }
    }, [newEntry.postcode]);

    useEffect(() => {
        if (
            contractConstants.contactCentreCreateScreenPostCodeLookup &&
            !postcodeServiceTeamMapping?.serviceId
        ) {
            if (contractConstants.defaultTeamId) {
                dispatch(loadTeam(contractConstants.defaultTeamId));
            }
        }
    }, [contractConstants, postcodeServiceTeamMapping]);

    useEffect(() => {
        if (
            currentTeam &&
            regExps.POSTCODE_R.test(newEntry.postcode) &&
            contractConstants.contactCentreCreateScreenPostCodeLookup &&
            !postcodeServiceTeamMapping?.serviceId
        ) {
            onUpdateService(currentTeam.serviceId, currentTeam.id);
        }
    }, [currentTeam, newEntry.postcode, contractConstants, postcodeServiceTeamMapping]);

    useEffect(() => {
        if (!pendingAutoTeamId || !teamsForService.length) return;

        const hasMappedTeamSelection =
            contractConstants.contactCentreCreateScreenPostCodeLookup &&
            postcodeServiceTeamMapping?.serviceId &&
            postcodeServiceTeamMapping?.teamId;
        const hasPendingTeam = teamsForService.some((team) => team.id === pendingAutoTeamId);

        if (!hasPendingTeam) {
            if (!hasMappedTeamSelection) {
                setPendingAutoTeamId('');
            }
            return;
        }

        onUpdate('teamId', pendingAutoTeamId);
        setPendingAutoTeamId('');
    }, [
        teamsForService,
        pendingAutoTeamId,
        contractConstants.contactCentreCreateScreenPostCodeLookup,
        postcodeServiceTeamMapping
    ]);

    useEffect(() => {
        if (Object.keys(contractConstants).length > 0 && newEntry.postcode) {
            if (
                contractConstants.contactCentreCreateScreenPostCodeLookup &&
                postcodeServiceTeamMapping?.serviceId &&
                postcodeServiceTeamMapping?.teamId
            ) {
                setPostcodeWarning('');
                onUpdateService(
                    postcodeServiceTeamMapping.serviceId,
                    postcodeServiceTeamMapping.teamId
                );
            } else if (regExps.POSTCODE_R.test(newEntry.postcode)) {
                setPostcodeWarning(
                    contractConstants.defaultTeamId
                        ? _POSTCODE_WARNING_TEXT.allocateDefault
                        : _POSTCODE_WARNING_TEXT.manualSelect
                );
            }
        }
    }, [postcodeServiceTeamMapping]);

    useEffect(() => {
        if (successMessage === _MESSAGE) {
            const navState = {
                contractId: newEntry.contractId,
                serviceId: newEntry.serviceId,
                teamId: newEntry.teamId,
                participantId: currentParticipant.id
            };
            clearParticipant();
            isButtonDisabled && setIsButtonDisabled(false);
            navigate('/appointment_slots_search', { state: navState });
        }
        if (validationErrors && Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            dispatch(clearValidationErrors());
        }
        if (businessLogicError && Object.keys(businessLogicError).length > 0) {
            setErrors(businessLogicError);
            dispatch(clearBusinessLogicError());
        }
    }, [successMessage, errorMessage, validationErrors, businessLogicError]);

    // HELPER FNS
    const clearParticipant = () => {
        setNewEntry(initialState);
        setErrors({});
        setResetKey(uuid);
    };

    const normalizePostcode = (value = '') => {
        const compact = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
        if (compact.length <= 3) return compact;
        return `${compact.slice(0, -3)} ${compact.slice(-3)}`;
    };

    const getUniqueIdString = (label) => {
        if (!newEntry.uniqueIdentifierType || newEntry.uniqueIdentifierType === 'TEMPORARY')
            return label;
        return `${names[newEntry.uniqueIdentifierType]} ${label}`;
    };

    const clearError = (key) => {
        if (key in errors) setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    const isServiceDisabled =
        !newEntry.contractId || // no contract selected
        (contractConstants.contactCentreCreateScreenPostCodeLookup && // postcode lookup enabled
            (!regExps.POSTCODE_R.test(newEntry.postcode) || // postcode is invalid
                (regExps.POSTCODE_R.test(newEntry.postcode) &&
                    postcodeServiceTeamMapping.serviceId &&
                    postcodeServiceTeamMapping.teamId))); // postcode is valid and mapping exists

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onPostcodeChange = (value) => {
        const formatted = value.toUpperCase();
        onUpdate('postcode', formatted);
        setErrors((prev) => getPostcodeInputErrors(formatted, prev));
    };

    const onUpdateContract = (chosenId) => {
        ['contractId', 'serviceId', 'teamId'].forEach((el) => clearError(el));
        setPendingAutoTeamId('');
        setNewEntry((prev) => ({
            ...prev,
            contractId: chosenId,
            serviceId: '',
            teamId: ''
        }));
    };

    const onUpdateService = (chosenId, teamId = '') => {
        ['serviceId', 'teamId'].forEach((el) => clearError(el));
        chosenId && dispatch(loadTeamsForService({ serviceIds: [chosenId] }));
        setNewEntry((prev) => ({
            ...prev,
            serviceId: chosenId,
            teamId: ''
        }));
        setPendingAutoTeamId(teamId || '');
    };

    const onUseTemporaryUniqueIdChange = (option) => {
        ['uniqueIdentifier', 'repeatUniqueIdentifier'].forEach((el) => clearError(el));
        setNewEntry((prev) =>
            option
                ? {
                      ...prev,
                      useTemporaryUniqueId: true,
                      uniqueIdentifierType: 'TEMPORARY',
                      uniqueIdentifier: 'TEMPORARY',
                      repeatUniqueIdentifier: 'TEMPORARY'
                  }
                : {
                      ...prev,
                      useTemporaryUniqueId: false,
                      uniqueIdentifierType: selectedContractDetails?.unique_identifier_type.name,
                      uniqueIdentifier: initialState.uniqueIdentifier,
                      repeatUniqueIdentifier: initialState.repeatUniqueIdentifier
                  }
        );
    };

    const onSubmit = (e) => {
        e.preventDefault();
        const newErrors = getSubmitErrors(newEntry);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            setIsButtonDisabled(false);
            return;
        }
        setIsButtonDisabled(true);
        dispatch(createParticipantContactCentre(newEntry, _MESSAGE));
    };

    // RENDER
    if (contractDetails?.length < 1) return <LoadingSpinner content={CONTRACT_DETAILS_ERROR} />;

    return (
        <div className={local.wrapper}>
            <div key={resetKey} className={form.formWrapper}>
                <form
                    onSubmit={onSubmit}
                    className={form.form}
                    data-testid="create-participant-form">
                    <h1 className={app.mainHeading}>Contact Centre - Create Participant</h1>
                    <h2 className={app.sectionHeading}>Contract Details</h2>
                    <div className={form.formSection}>
                        <div className={form.formColumn}>
                            <SingleSelect
                                id={'contractId'}
                                label={'Contract'}
                                placeholder="Select Contract..."
                                disabled={disableFields}
                                mandatory={true}
                                menuItems={contractDetails}
                                selectedId={newEntry.contractId}
                                selected={getObjFromId(contractDetails, newEntry.contractId)}
                                error={errors.contractId}
                                onChange={(chosenId) => onUpdateContract(chosenId)}
                            />
                            {contractConstants.contactCentreCreateScreenPostCodeLookup && (
                                <TextInputField
                                    id={'postcode'}
                                    label={'Postcode'}
                                    placeholder={'Enter postcode'}
                                    disabled={disableFields}
                                    mandatory={true}
                                    maxLength={MAX_POSTCODE}
                                    value={newEntry.postcode || ''}
                                    error={errors.postcode}
                                    onPaste={(e) => {
                                        e.preventDefault();
                                        onPostcodeChange(normalizePostcode(e.clipboardData?.getData('text') || ''));
                                    }}
                                    onChange={(e) => onPostcodeChange(e.target.value)}
                                />
                            )}
                            {contractConstants.contactCentreCreateScreenPostCodeLookup &&
                                postcodeWarning &&
                                !errors?.postcode?.error && (
                                    <div className={`${form.amberWarning} ${local.alignWarning}`}>
                                        <WarningIcon /> {postcodeWarning}
                                    </div>
                                )}
                            <SingleSelect
                                id={'serviceId'}
                                label={'Service'}
                                placeholder="Select Service..."
                                disabled={disableFields || isServiceDisabled}
                                mandatory={true}
                                menuItems={services}
                                selectedId={newEntry.serviceId}
                                selected={getObjFromId(services, newEntry.serviceId)}
                                error={errors.serviceId}
                                onChange={(chosenId) => onUpdateService(chosenId)}
                            />
                            <SingleSelect
                                id={'teamId'}
                                label={'Team'}
                                placeholder="Select Team..."
                                disabled={disableFields || !newEntry.serviceId}
                                mandatory={true}
                                menuItems={teamsForService}
                                selectedId={newEntry.teamId}
                                selected={getObjFromId(teamsForService, newEntry.teamId)}
                                error={errors.teamId}
                                onChange={(chosenId) => onUpdate('teamId', chosenId)}
                            />
                        </div>
                        <div className={form.formColumn}>
                            {newEntry.contractId &&
                                contractConstants?.contactCentreCreateScreenUseTemporaryUniqueId && (
                                    <RadioButtons
                                        id="useTemporaryUniqueId"
                                        label="Use Temporary Unique ID"
                                        disabled={
                                            !(
                                                newEntry.contractId &&
                                                contractConstants.contactCentreCreateScreenUseTemporaryUniqueId
                                            )
                                        }
                                        value={newEntry.uniqueIdentifierType === 'TEMPORARY'}
                                        onChange={(option) => onUseTemporaryUniqueIdChange(option)}
                                    />
                                )}
                            <TextInputField
                                id={'uniqueId'}
                                label={getUniqueIdString('Unique ID')}
                                placeholder={'Enter unique identifier'}
                                disabled={
                                    disableFields || newEntry.uniqueIdentifierType === 'TEMPORARY'
                                }
                                mandatory={true}
                                value={newEntry.uniqueIdentifier || ''}
                                error={errors.uniqueIdentifier}
                                onPaste={(e) => e.preventDefault()}
                                onChange={(e) => onUpdate('uniqueIdentifier', e.target.value)}
                            />
                            <TextInputField
                                id={'repeatUniqueId'}
                                label={getUniqueIdString('Repeat Unique ID')}
                                placeholder={'Repeat unique identifier'}
                                disabled={
                                    disableFields || newEntry.uniqueIdentifierType === 'TEMPORARY'
                                }
                                mandatory={true}
                                value={newEntry.repeatUniqueIdentifier || ''}
                                error={errors.repeatUniqueIdentifier}
                                onPaste={(e) => e.preventDefault()}
                                onChange={(e) => onUpdate('repeatUniqueIdentifier', e.target.value)}
                            />
                        </div>
                    </div>

                    {newEntry.serviceId && newEntry.teamId && (
                        <>
                            <h2 className={app.sectionHeading}>General Details</h2>
                            <div className={form.formSection}>
                                <div className={form.formColumn}>
                                    <TextInputField
                                        id={'firstName'}
                                        label={'First Name'}
                                        placeholder={'Enter first name'}
                                        disabled={disableFields}
                                        mandatory={true}
                                        maxLength={MAX_NAME}
                                        value={newEntry.firstName || ''}
                                        error={errors.firstName}
                                        onChange={(e) => onUpdate('firstName', e.target.value)}
                                    />
                                    <TextInputField
                                        id={'middleName'}
                                        label={'Middle Name'}
                                        placeholder={'Enter middle name'}
                                        disabled={disableFields}
                                        maxLength={MAX_NAME}
                                        value={newEntry.middleName || ''}
                                        error={errors.middleName}
                                        onChange={(e) => onUpdate('middleName', e.target.value)}
                                    />
                                    <TextInputField
                                        id={'lastName'}
                                        label={'Last Name'}
                                        placeholder={'Enter last name'}
                                        disabled={disableFields}
                                        maxLength={MAX_NAME}
                                        value={newEntry.lastName || ''}
                                        error={errors.lastName}
                                        onChange={(e) => onUpdate('lastName', e.target.value)}
                                    />
                                    <TextInputField
                                        id="primaryPhoneNumber"
                                        type="tel"
                                        label="Phone Number"
                                        placeholder={'Enter phone number'}
                                        disabled={disableFields}
                                        maxLength={MAX_PHONE}
                                        value={newEntry.primaryPhoneNumber || ''}
                                        error={errors.primaryPhoneNumber}
                                        onChange={(e) => {
                                            onUpdate('primaryPhoneNumber', e.target.value);
                                        }}
                                    />
                                    <TextInputField
                                        id={'emailAddress'}
                                        label={'Email Address'}
                                        placeholder={'Enter email address'}
                                        disabled={disableFields}
                                        maxLength={MAX_EMAIL}
                                        value={newEntry.emailAddress || ''}
                                        error={errors.emailAddress}
                                        onChange={(e) => onUpdate('emailAddress', e.target.value)}
                                    />
                                </div>
                                <div className={form.formColumn}>
                                    <TextInputField
                                        id={'address1'}
                                        label={'Address Line 1'}
                                        placeholder={'Enter address line 1'}
                                        disabled={disableFields}
                                        maxLength={ADDRESS_LINE_1_MAX_LENGTH}
                                        value={newEntry.address1 || ''}
                                        error={errors.address1}
                                        onChange={(e) => onUpdate('address1', e.target.value)}
                                    />
                                    <TextInputField
                                        id={'address2'}
                                        label={'Address Line 2'}
                                        placeholder={'Enter address line 2'}
                                        disabled={disableFields}
                                        maxLength={ADDRESS_LINE_2_MAX_LENGTH}
                                        value={newEntry.address2 || ''}
                                        error={errors.address2}
                                        onChange={(e) => onUpdate('address2', e.target.value)}
                                    />
                                    <TextInputField
                                        id={'address3'}
                                        label={'Address Line 3'}
                                        placeholder={'Enter address line 3'}
                                        disabled={disableFields}
                                        maxLength={ADDRESS_LINE_3_MAX_LENGTH}
                                        value={newEntry.address3 || ''}
                                        error={errors.address3}
                                        onChange={(e) => onUpdate('address3', e.target.value)}
                                    />
                                    <TextInputField
                                        id={'city'}
                                        label={'City'}
                                        placeholder={'Enter city'}
                                        disabled={disableFields}
                                        maxLength={CITY_MAX_LENGTH}
                                        value={newEntry.city || ''}
                                        error={errors.city}
                                        onChange={(e) => onUpdate('city', e.target.value)}
                                    />
                                    {!contractConstants.contactCentreCreateScreenPostCodeLookup && (
                                        <TextInputField
                                            id={'postcode'}
                                            label={'Postcode'}
                                            placeholder={'Enter postcode'}
                                            disabled={disableFields}
                                            mandatory={true}
                                            maxLength={MAX_POSTCODE}
                                            value={newEntry.postcode || ''}
                                            error={errors.postcode}
                                            onPaste={(e) => {
                                                e.preventDefault();
                                                onPostcodeChange(normalizePostcode(e.clipboardData?.getData('text') || ''));
                                            }}
                                            onChange={(e) => onPostcodeChange(e.target.value)}
                                        />
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                    <Button
                        id="createParticipant"
                        content="Create"
                        disabled={isButtonDisabled || disableFields}
                    />
                    {errors.businessLogicError?.error && (
                        <ScreenError error={errors.businessLogicError} clearError={clearError} />
                    )}
                </form>
            </div>
        </div>
    );
};

export default CreateParticipantContactCentre;
